import React from "react";
import { Form, Input, Button, message, ConfigProvider } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Auth } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./LoginForm.form";

const authController = new Auth();

const REVI_ROLES = new Set(["revi", "therapist", "patient"]);
const EDUCA360_URL = "https://educa360.net";

const formTheme = {
  token: {
    colorPrimary: "#2563eb",
    borderRadius: 8,
  },
};

function decodeTokenRole(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.role;
  } catch {
    return null;
  }
}

export function LoginForm() {
  // Dos namespaces: el propio de acceso y el comun, del que salen las etiquetas
  // de correo y contrasena que se repiten por todo el portal.
  const { t } = useTranslation(["auth", "common"]);
  const { login } = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const response = await authController.login(formValue);

        const role = decodeTokenRole(response.access);
        // vertical viene del backend; permite identificar institutions REVI
        // legacy con role:"institution" sin migrar BD.
        const isReviInstitution =
          role === "institution" && response.vertical === "revi";
        const allowed = (role && REVI_ROLES.has(role)) || isReviInstitution;
        if (!allowed) {
          authController.removeTokens();
          // La URL va DENTRO de la frase: en frances "depuis {{url}}" no cae
          // en el mismo sitio que "desde {{url}}".
          message.warning(
            t("login.cuentaDeEduca360", { url: EDUCA360_URL }),
            6,
          );
          return;
        }

        authController.setAccessToken(response.access);
        authController.setRefreshToken(response.refresh);
        await login(response.access);
        navigate("/");
      } catch (error) {
        console.error(error);
        // El mensaje del backend manda si viene; el nuestro es solo el respaldo.
        const msg =
          error?.message || error?.msg || t("auth:login.errorCredentials");
        message.error(msg);
      }
    },
  });

  return (
    <ConfigProvider theme={formTheme}>
      <Form
        layout="vertical"
        onFinish={formik.handleSubmit}
        requiredMark={false}
        className="login-form-antd"
      >
        <Form.Item
          validateStatus={formik.errors.email ? "error" : ""}
          help={formik.errors.email}
          style={{ marginBottom: 20 }}
        >
          <Input
            size="large"
            prefix={<UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
            placeholder={t("common:fields.email")}
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            autoComplete="email"
          />
        </Form.Item>

        <Form.Item
          validateStatus={formik.errors.password ? "error" : ""}
          help={formik.errors.password}
          style={{ marginBottom: 24 }}
        >
          <Input.Password
            size="large"
            prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
            type="password"
            placeholder={t("common:fields.password")}
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            autoComplete="current-password"
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={formik.isSubmitting}
            style={{
              fontWeight: 600,
              height: 48,
              boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.3)",
            }}
          >
            {t("auth:login.submit")}
          </Button>
        </Form.Item>

        <div style={{ textAlign: "center" }}>
          <a href="/forgot-password" style={{ color: "#64748b", fontSize: 14 }}>
            {t("auth:login.forgotPassword")}
          </a>
        </div>
      </Form>
    </ConfigProvider>
  );
}
